"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";

export default function AboutCTA() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    const el = sectionRef.current;
    if (el) el.querySelectorAll(".reveal").forEach((i) => observer.observe(i));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-28 bg-[#0A1628] overflow-hidden">
      <div className="absolute top-0 left-0 right-0 gold-line" />

      {/* Background glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse, rgba(201,168,76,0.07) 0%, transparent 70%)",
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center space-y-8">
        <div className="reveal inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[rgba(201,168,76,0.3)] bg-[rgba(201,168,76,0.06)]">
          <span className="text-[11px] font-display font-600 uppercase tracking-[0.18em] text-gold">
            Ready to Transform Your Operations?
          </span>
        </div>

        <h2 className="reveal delay-100 text-4xl md:text-6xl font-display font-700 text-cream leading-tight tracking-tight">
          Let&apos;s Build Something{" "}
          <span className="text-shimmer">Exceptional</span>{" "}
          Together.
        </h2>

        <p className="reveal delay-200 text-[17px] font-body text-cream/55 leading-relaxed max-w-2xl mx-auto">
          Our team is ready to analyze your current operations and develop a customized strategy for measurable, sustainable growth.
        </p>

        <div className="reveal delay-300 flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            href="/homepage#contact"
            className="btn-primary px-10 py-4 text-sm rounded-sm font-display inline-flex items-center gap-2 justify-center"
          >
            Get a Free Consultation
            <Icon name="ArrowRightIcon" size={16} className="text-navy" />
          </Link>
          <Link
            href="/homepage#services"
            className="btn-outline px-10 py-4 text-sm rounded-sm font-display inline-flex items-center gap-2 justify-center"
          >
            View Our Services
          </Link>
        </div>

        {/* Trust badges */}
        <div className="reveal delay-400 flex flex-wrap items-center justify-center gap-8 pt-4">
          {[
            { icon: "ShieldCheckIcon", text: "Trusted Globally" },
            { icon: "ClockIcon", text: "12+ Years Experience" },
            { icon: "UserGroupIcon", text: "97+ Client Engagements" },
          ].map((badge) => (
            <div key={badge.text} className="flex items-center gap-2">
              <Icon name={badge.icon as "ShieldCheckIcon"} size={15} className="text-gold/70" />
              <span className="text-[12px] font-display font-500 uppercase tracking-wider text-cream/45">
                {badge.text}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}